import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiMapPin, FiGlobe, FiCamera, FiTrash2 } from 'react-icons/fi';
import api from '../../libs/axios';
import toast from 'react-hot-toast';

enum PitchType {
    ROUGH = "rough",
    CEMENT = "cement",
    TURF = "turf",
    MATTING = "matting",
    ASTROTURF = "astroturf",
}

enum GroundStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    DRAFT = 'draft',
    PENDING = 'pending'
}

enum GroundSource {
    API = 'api',
    MANUAL = 'manual'
}

interface EditGroundOffcanvasProps {
    groundId: string;
    showEditGroundCanvas: boolean;
    setShowEditGroundCanvas: (show: boolean) => void;
    onGroundUpdated?: () => void;
}

interface GroundFormData {
    name: string;
    country: string;
    city: string;
    address: string;
    pitch_type: PitchType | string;
    lighting_available: boolean;
    capacity: string;
    status: GroundStatus;
    source: GroundSource;
    latitude: string;
    longitude: string;
    images: File[];
}

const EditGroundOffcanvas: React.FC<EditGroundOffcanvasProps> = ({
    groundId,
    showEditGroundCanvas,
    setShowEditGroundCanvas,
    onGroundUpdated
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);

    const [formData, setFormData] = useState<GroundFormData>({
        name: '',
        country: '',
        city: '',
        address: '',
        pitch_type: '',
        lighting_available: false,
        capacity: '',
        status: GroundStatus.DRAFT,
        source: GroundSource.MANUAL,
        latitude: '',
        longitude: '',
        images: []
    });

    const [errors, setErrors] = useState<Partial<GroundFormData>>({});

    const countries = [
        'India', 'United States', 'United Kingdom', 'Australia', 'New Zealand',
        'South Africa', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'West Indies'
    ];

    const pitchTypes = [
        { value: PitchType.TURF, label: 'Turf' },
        { value: PitchType.MATTING, label: 'Matting' },
        { value: PitchType.CEMENT, label: 'Cement' },
        { value: PitchType.ROUGH, label: 'Rough' },
        { value: PitchType.ASTROTURF, label: 'Astroturf' },
    ];

    // Fetch ground data when component opens
    useEffect(() => {
        if (showEditGroundCanvas && groundId) {
            fetchGroundData();
        }
    }, [showEditGroundCanvas, groundId]);

    const fetchGroundData = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/grounds/${groundId}`);
            const ground = response.data.ground;

            setFormData({
                name: ground.name || '',
                country: ground.country || '',
                city: ground.city || '',
                address: ground.address || '',
                pitch_type: ground.pitch_type || '',
                lighting_available: ground.lighting_available || false,
                capacity: ground.capacity ? ground.capacity.toString() : '',
                status: ground.status || GroundStatus.DRAFT,
                source: ground.source || GroundSource.MANUAL,
                latitude: ground.latitude ? ground.latitude.toString() : '',
                longitude: ground.longitude ? ground.longitude.toString() : '',
                images: []
            });

            // Set existing images
            if (ground.image_urls && Array.isArray(ground.image_urls)) {
                setExistingImageUrls(ground.image_urls);
            }

        } catch (error: any) {
            console.error('Error fetching ground data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when user starts typing
        if (errors[name as keyof GroundFormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle multiple image upload
    const handleImagesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);

        if (files.length === 0) return;

        // Validate total number of images (existing + new, max 5)
        const totalImages = existingImageUrls.length + formData.images.length + files.length;
        if (totalImages > 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }

        // Validate each file
        for (const file of files) {
            if (!file.type.startsWith('image/')) {
                toast.error('Please select valid image files only');
                return;
            }
            if (file.size > 2 * 1024 * 1024) { // 2MB limit
                toast.error('Each image should be less than 2MB');
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...files]
        }));

        // Create previews
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreviews(prev => [...prev, e.target?.result as string]);
            };
            reader.readAsDataURL(file);
        });
    };

    // Remove new image
    const removeNewImage = (index: number) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    // Remove existing image
    const removeExistingImage = (index: number) => {
        setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
    };

    // Upload files to server
    const uploadFiles = async (files: File[], type: string): Promise<string[]> => {
        const uploadFormData = new FormData();
        uploadFormData.append('type', type);

        files.forEach(file => {
            uploadFormData.append('files', file);
        });

        try {
            const response = await api.post('/media/upload', uploadFormData);
            const uploadedFiles = response.data?.files || [];

            return uploadedFiles.map((file: any) => file.url);
        } catch (error) {
            console.error('Error uploading files:', error);
            throw new Error('Failed to upload files');
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<GroundFormData> = {};

        if (!formData.name.trim()) newErrors.name = 'Ground name is required';
        if (!formData.country.trim()) newErrors.country = 'Country is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.pitch_type) newErrors.pitch_type = 'Pitch type is required';

        // Capacity validation (if provided)
        if (formData.capacity && (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0)) {
            newErrors.capacity = 'Capacity must be a valid positive number';
        }

        // Latitude validation (if provided)
        if (formData.latitude && (isNaN(Number(formData.latitude)) || Number(formData.latitude) < -90 || Number(formData.latitude) > 90)) {
            newErrors.latitude = 'Latitude must be a valid number between -90 and 90';
        }

        // Longitude validation (if provided)
        if (formData.longitude && (isNaN(Number(formData.longitude)) || Number(formData.longitude) < -180 || Number(formData.longitude) > 180)) {
            newErrors.longitude = 'Longitude must be a valid number between -180 and 180';
        }

        // Coordinate pair validation
        if ((formData.latitude && !formData.longitude) || (!formData.latitude && formData.longitude)) {
            newErrors.latitude = 'Both latitude and longitude must be provided together';
            newErrors.longitude = 'Both latitude and longitude must be provided together';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        try {
            let newImageUrls: string[] = [];

            // Upload new images if any
            if (formData.images.length > 0) {
                toast.loading('Uploading images...');
                newImageUrls = await uploadFiles(formData.images, 'ground_images');
                toast.dismiss();
            }

            // Combine existing and new image URLs
            const allImageUrls = [...existingImageUrls, ...newImageUrls];

            // Prepare ground data
            const groundData: any = {
                name: formData.name.trim(),
                country: formData.country.trim(),
                city: formData.city.trim(),
                pitch_type: formData.pitch_type,
                lighting_available: formData.lighting_available,
                status: formData.status,
                source: formData.source
            };

            // Add optional fields
            if (formData.address.trim()) groundData.address = formData.address.trim();
            if (formData.capacity) groundData.capacity = parseInt(formData.capacity);
            if (allImageUrls.length > 0) groundData.image_urls = allImageUrls;
            if (formData.latitude) groundData.latitude = parseFloat(formData.latitude);
            if (formData.longitude) groundData.longitude = parseFloat(formData.longitude);

            await api.put(`/grounds/${groundId}`, groundData);
            toast.success('Ground updated successfully!');

            // Call callback and close offcanvas
            if (onGroundUpdated) {
                onGroundUpdated();
            }
            handleClose();

        } catch (error: any) {
            console.error('Error updating ground:', error);
            let errorMessage = 'Failed to update ground';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message === 'Failed to upload files') {
                errorMessage = 'Failed to upload images. Please try again.';
            }

            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            country: '',
            city: '',
            address: '',
            pitch_type: '',
            lighting_available: false,
            capacity: '',
            status: GroundStatus.DRAFT,
            source: GroundSource.MANUAL,
            latitude: '',
            longitude: '',
            images: []
        });
        setImagePreviews([]);
        setExistingImageUrls([]);
        setErrors({});

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = () => {
        if (!submitting) {
            resetForm();
            setShowEditGroundCanvas(false);
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${showEditGroundCanvas ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${showEditGroundCanvas ? 'translate-x-0' : 'translate-x-full'
                }`}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiMapPin className="mr-2" />
                        Edit Ground
                    </h2>
                    <button
                        onClick={handleClose}
                        disabled={submitting}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <div className="flex flex-col h-full">
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {/* Basic Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Ground Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            disabled={submitting}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.name ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter ground name"
                                        />
                                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                <FiGlobe className="inline mr-1" />
                                                Country *
                                            </label>
                                            <select
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.country ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            >
                                                <option value="">Select Country</option>
                                                {countries.map(country => (
                                                    <option key={country} value={country}>{country}</option>
                                                ))}
                                            </select>
                                            {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                City *
                                            </label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.city ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter city"
                                            />
                                            {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FiMapPin className="inline mr-1" />
                                            Address / Landmark
                                        </label>
                                        <textarea
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            disabled={submitting}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                                            placeholder="Enter full address or landmark"
                                        />
                                    </div>
                                </div>

                                {/* Ground Details */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Ground Details</h3>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Pitch Type *
                                            </label>
                                            <select
                                                name="pitch_type"
                                                value={formData.pitch_type}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.pitch_type ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            >
                                                <option value="">Select Pitch Type</option>
                                                {pitchTypes.map(type => (
                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                ))}
                                            </select>
                                            {errors.pitch_type && <p className="text-red-500 text-xs mt-1">{errors.pitch_type}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Approximate Capacity
                                            </label>
                                            <input
                                                type="number"
                                                name="capacity"
                                                value={formData.capacity}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.capacity ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter capacity"
                                                min="0"
                                            />
                                            {errors.capacity && <p className="text-red-500 text-xs mt-1">{errors.capacity}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="lighting_available"
                                                checked={formData.lighting_available}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                                            />
                                            <label className="ml-2 block text-sm text-gray-900">
                                                Lighting Available
                                            </label>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status
                                            </label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                                            >
                                                <option value={GroundStatus.DRAFT}>Draft</option>
                                                <option value={GroundStatus.PENDING}>Pending</option>
                                                <option value={GroundStatus.ACTIVE}>Active</option>
                                                <option value={GroundStatus.INACTIVE}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Location Coordinates */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Latitude
                                            </label>
                                            <input
                                                type="text"
                                                name="latitude"
                                                value={formData.latitude}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.latitude ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter latitude (e.g., 12.9716)"
                                            />
                                            {errors.latitude && <p className="text-red-500 text-xs mt-1">{errors.latitude}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Longitude
                                            </label>
                                            <input
                                                type="text"
                                                name="longitude"
                                                value={formData.longitude}
                                                onChange={handleInputChange}
                                                disabled={submitting}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.longitude ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter longitude (e.g., 77.5946)"
                                            />
                                            {errors.longitude && <p className="text-red-500 text-xs mt-1">{errors.longitude}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* Ground Images */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Ground Images</h3>

                                    {/* Multiple Images */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <FiCamera className="inline mr-1" />
                                            Ground Images (Max 5 total)
                                        </label>

                                        {/* Existing Images */}
                                        {existingImageUrls.length > 0 && (
                                            <div className="mb-4">
                                                <p className="text-sm text-gray-600 mb-2">Current Images:</p>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    {existingImageUrls.map((imageUrl, index) => (
                                                        <div key={`existing-${index}`} className="relative">
                                                            <img
                                                                src={imageUrl}
                                                                alt={`Existing ${index + 1}`}
                                                                className="w-full h-24 object-cover rounded-lg border"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeExistingImage(index)}
                                                                disabled={submitting}
                                                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-50"
                                                            >
                                                                <FiTrash2 className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* New Image Previews */}
                                        {imagePreviews.length > 0 && (
                                            <div className="mb-4">
                                                <p className="text-sm text-gray-600 mb-2">New Images:</p>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    {imagePreviews.map((preview, index) => (
                                                        <div key={`new-${index}`} className="relative">
                                                            <img
                                                                src={preview}
                                                                alt={`New Preview ${index + 1}`}
                                                                className="w-full h-24 object-cover rounded-lg border"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeNewImage(index)}
                                                                disabled={submitting}
                                                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-50"
                                                            >
                                                                <FiTrash2 className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Upload Area */}
                                        {(existingImageUrls.length + formData.images.length) < 5 && (
                                            <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                                                <div className="space-y-1 text-center">
                                                    <div className="flex text-sm text-gray-600">
                                                        <label
                                                            htmlFor="file-upload"
                                                            className="relative w-full text-center cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                                                        >
                                                            <p className="text-center">Upload files</p>
                                                            <input
                                                                id="file-upload"
                                                                ref={fileInputRef}
                                                                name="images"
                                                                type="file"
                                                                multiple
                                                                accept="image/*"
                                                                onChange={handleImagesUpload}
                                                                disabled={submitting}
                                                                className="sr-only"
                                                            />
                                                        </label>
                                                    </div>
                                                    <p className="text-xs text-gray-500">
                                                        PNG, JPG up to 5MB each ({5 - (existingImageUrls.length + formData.images.length)} remaining)
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Footer with action buttons */}
                            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 mb-20">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={submitting}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm w-full font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm w-full font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                >
                                    {submitting ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Updating...
                                        </div>
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default EditGroundOffcanvas;